import type { ReactNode } from 'react'
import { Toggle } from './toggle'

/**
 * The one settings row (Nov 4 2026, the Coinbase pattern): the label with its description under it on the left, the
 * control on the right, rows divided by hairlines inside a SettingList. A row with `on` carries a switch and folds its
 * children away while it is off; `preview` sits beside the switch while it is on. The Settings page and the strategy
 * wizard's options both read this way; the wizard's card-per-option block and the page's ad-hoc lists are gone.
 */
export function SettingList({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`divide-y divide-line ${className}`}>{children}</div>
}

export function SettingRow({
  title,
  description,
  icon,
  preview,
  control,
  on,
  onToggle,
  disabled,
  children,
}: {
  title: ReactNode
  description?: ReactNode
  /** a logo or icon before the label */
  icon?: ReactNode
  /** the range the setting produces, shown beside the control while it is on */
  preview?: string
  /** a control other than the switch: a value, a button, a dropdown */
  control?: ReactNode
  /** omit for a row without a switch */
  on?: boolean
  onToggle?: (on: boolean) => void
  disabled?: boolean
  children?: ReactNode
}) {
  const alwaysOn = on === undefined
  const open = alwaysOn || on
  return (
    <div className="grid gap-3 py-3.5 text-sm">
      <div className="flex items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-2.5">
          {icon}
          <div className="min-w-0">
            <div className="font-medium">{title}</div>
            {description ? <div className="mt-0.5 text-xs text-muted">{description}</div> : null}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {preview && open ? <span className="text-muted tabular-nums">{preview}</span> : null}
          {control}
          {!alwaysOn && onToggle ? <Toggle checked={on} onChange={onToggle} label={typeof title === 'string' ? title : 'Setting'} disabled={disabled} /> : null}
        </div>
      </div>
      {open && children ? <div>{children}</div> : null}
    </div>
  )
}
